// import React, { createContext, useContext, useState, useEffect } from 'react';
// import { supabase } from '../lib/supabase';

// const AuthContext = createContext();

// export const useAuth = () => {
//   const context = useContext(AuthContext);
//   if (!context) {
//     throw new Error('useAuth must be used within an AuthProvider');
//   }
//   return context;
// };

// export const AuthProvider = ({ children }) => {
//   const [user, setUser] = useState(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     // Check active sessions and sets the user
//     const getSession = async () => {
//       const { data: { session } } = await supabase.auth.getSession();
//       setUser(session?.user ?? null);
//       setLoading(false);
//     };

//     getSession();

//     // Listen for changes to auth state
//     const { data: { subscription } } = supabase.auth.onAuthStateChange(
//       async (event, session) => {
//         setUser(session?.user ?? null);
//         setLoading(false);
        
//         // Optional: Handle specific auth events
//         if (event === 'SIGNED_IN') {
//           console.log('User signed in!');
//         } else if (event === 'SIGNED_OUT') {
//           console.log('User signed out!');
//         }
//       }
//     );

//     return () => subscription.unsubscribe();
//   }, []);

//   // Sign up function
//   const signUp = async (email, password, firstName, lastName) => {
//     const { data, error } = await supabase.auth.signUp({
//       email,
//       password,
//       options: {
//         data: {
//           first_name: firstName,
//           last_name: lastName
//         }
//       }
//     });

//     if (error) {
//       console.error('Signup error:', error);
//       throw error;
//     }
//     return data;
//   };

//   // Sign in function
//   const signIn = async (email, password) => {
//     const { data, error } = await supabase.auth.signInWithPassword({
//       email,
//       password
//     });

//     if (error) {
//       console.error('Signin error:', error);
//       throw error;
//     }
//     return data;
//   };

//   // Sign out function
//   // const signOut = async () => {
//   //   const { error } = await supabase.auth.signOut();
//   //   if (error) {
//   //     console.error('Signout error:', error);
//   //     throw error;
//   //   }
//   // };
//   const signOut = async () => {
//     try {
//       const { error } = await supabase.auth.signOut();
      
//       // Don't throw error if session is already missing
//       if (error && error.message !== "Auth session missing!") {
//         throw error;
//       }
      
//       console.log('Signed out successfully');
//     } catch (error) {
//       // If it's a session missing error, just log it and continue
//       if (error.message === "Auth session missing!") {
//         console.log('Session already expired, user logged out locally');
//       } else {
//         console.error('Signout error:', error);
//         throw error;
//       }
//     }
//   };

//   // Password reset function
//   const resetPassword = async (email) => {
//     const { error } = await supabase.auth.resetPasswordForEmail(email);
//     if (error) throw error;
//   };

//   const value = {
//     user,
//     signUp,
//     signIn,
//     signOut,
//     resetPassword,
//     loading
//   };

//   return (
//     <AuthContext.Provider value={value}>
//       {!loading && children}
//     </AuthContext.Provider>
//   );
  
// };


import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, getAuthRedirectUrl } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
        setAccessToken(session?.access_token ?? null);
      } catch (error) {
        console.error('Error restoring auth session:', error);
        setUser(null);
        setAccessToken(null);
      } finally {
        setLoading(false);
      }
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setAccessToken(session?.access_token ?? null);
        setLoading(false);
        
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setUser(null);
          setAccessToken(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password, firstName, lastName) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: getAuthRedirectUrl(),
        data: {
          first_name: firstName,
          last_name: lastName
        }
      }
    });

    if (error) {
      console.error('Signup error:', error);
      throw error;
    }
    return data;
  };

  const resendVerificationEmail = async (email) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: getAuthRedirectUrl(),
      }
    });
    if (error) throw error;
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      console.error('Signin error:', error);
      throw error;
    }
    return data;
  };

  const signOut = async () => {
    try {
      setUser(null);
      setAccessToken(null);
      const { error } = await supabase.auth.signOut();

      if (error && error.message !== "Auth session missing!") {
        throw error;
      }
    } catch (error) {
      if (error.message !== "Auth session missing!") {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
        setAccessToken(session?.access_token ?? null);
        console.error('Signout error:', error);
        throw error;
      }
    }
  };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getAuthRedirectUrl(),
    });
    if (error) throw error;
  };

  const value = {
    user,
    signUp,
    signIn,
    signOut,
    resetPassword,
    resendVerificationEmail,
    accessToken,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
